import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import selectorStateComparison from 'Redux/utilities/selectorStateComparison';
import ErrorPanel from 'ViatoUi/Extranet/DataDisplay/ErrorPanel';
import Modal from 'ViatoUi/Extranet/Surfaces/Modal';
import Loader from 'ViatoUi/Shared/Loader';

import Footer from './layouts/Footer';
import Section from './layouts/Section';
import Share from './layouts/Share';
import { Step1, Step2, Step3, Step4, Step5 } from './pages';
import Login from './pages/Login';
import Constants from './shared/constants';

const ContractGenerator = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation('contractGenerator');

	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const sessionId = urlParams.get('cgs');
	const login = urlParams.get('login');
	const knownErrorCodes = [520];

	const activeStep = useSelector(contractGeneratorSelectors.getActiveStep);
	const products = useSelector(contractGeneratorSelectors.getProducts);
	const extensions = useSelector(contractGeneratorSelectors.getExtensions);
	const { selectedProduct: step1IsBlocked = false } = useSelector(contractGeneratorSelectors.getBlockedFields);
	const dataToSubmit = useSelector(contractGeneratorSelectors.getDataToSubmit);
	const savedSessionId = useSelector(contractGeneratorSelectors.getSessionId);

	const [fetchCallsPending, fetchCallsError, fetchCallsErrorCode, fetchCallsErrorMessage] = useSelector(
		contractGeneratorSelectors.getFetchApiCalls(),
		(formerState, currentState) => selectorStateComparison(formerState, currentState)
	);

	// log data if the tab or window is closed
	window.onbeforeunload = () => {
		dispatch(
			contractGeneratorOperations.logData({
				dataToSubmit,
				userSessionId: sessionId,
				action: 'window_closed'
			})
		);
	};

	// log data in case any change happened
	useEffect(() => {
		dispatch(
			contractGeneratorOperations.logData({
				dataToSubmit,
				userSessionId: sessionId,
				action: 'data_change'
			})
		);
	}, [
		dataToSubmit.selectedAccommodationType,
		dataToSubmit.selectedRooms,
		dataToSubmit.selectedPaymentType,
		dataToSubmit.selectedContractTermInMonths,
		activeStep,
		savedSessionId
	]);

	window.onhashchange = e => {
		const newStep = e.newURL.split('#')[1] || '';

		if (Constants.STEPS_ORDER.includes(newStep) && activeStep !== newStep && (!step1IsBlocked || newStep !== Constants.STEPS.STEP1)) {
			dispatch(contractGeneratorOperations.setActiveStep(newStep));
		}

		if (newStep === '' && activeStep !== Constants.STEPS.STEP1 && !step1IsBlocked) {
			dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP1));
		}

		if (newStep === '' && activeStep !== Constants.STEPS.STEP2 && step1IsBlocked) {
			dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP2));
		}
	};

	const getStepContent = () => {
		switch (activeStep) {
			case Constants.STEPS.STEP1:
				return <Step1 />;

			case Constants.STEPS.STEP2:
				return <Step2 />;

			case Constants.STEPS.STEP3:
				return <Step3 />;

			case Constants.STEPS.STEP4:
				return <Step4 />;

			case Constants.STEPS.STEP5:
				return <Step5 />;

			case Constants.STEPS.LOGIN:
				return <Login />;

			default:
				return <Step1 />;
		}
	};

	useEffect(() => {
		if (sessionId) {
			dispatch(contractGeneratorOperations.fetchSession(sessionId));
		}

		dispatch(contractGeneratorOperations.fetchProducts(sessionId || 'noSession'));
		dispatch(contractGeneratorOperations.fetchExtensions(sessionId || 'noSession'));
		dispatch(contractGeneratorOperations.fetchEnableStep2Form());
		dispatch(contractGeneratorOperations.fetchForms());
		dispatch(contractGeneratorOperations.fetchFormFields());
		dispatch(contractGeneratorOperations.fetchConstants());

		if (login) {
			dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.LOGIN));
		}

		const userSessionId = Cookies.get('userSessionId');
		if (userSessionId) {
			dispatch(contractGeneratorOperations.checkUserSession(userSessionId));
		}
	}, []);

	if (fetchCallsError && !knownErrorCodes.includes(fetchCallsErrorCode)) {
		return (
			<Section isFirstPage>
				<ErrorPanel
					errorCode={fetchCallsErrorCode}
					errorMessage={fetchCallsErrorMessage}
					userMessage={t('app.errorMessages.unknown.description')}
				/>
			</Section>
		);
	}

	if (fetchCallsPending || Object.keys(products).length === 0 || Object.keys(extensions).length === 0) {
		return <Loader />;
	}

	return (
		<>
			<Modal
				title={fetchCallsErrorCode ? t(`app.errorMessages.${fetchCallsErrorCode}.title`) : ''}
				visible={fetchCallsError}
				onClose={() => {
					const url = window.location.href;
					const urlWithoutParam = url.split('cgs=')[0].split('#')[0];
					window.location.href = urlWithoutParam;
				}}
			>
				{fetchCallsErrorCode ? t(`app.errorMessages.${fetchCallsErrorCode}.description`) : ''}
			</Modal>
			{getStepContent()}
			{activeStep === Constants.STEPS.STEP2 || activeStep === Constants.STEPS.STEP3 ? <Footer /> : <></>}
			{(activeStep === Constants.STEPS.STEP2 || activeStep === Constants.STEPS.STEP3) && <Share />}
		</>
	);
};

export default ContractGenerator;
